import React, { useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ChevronLeft, Clock, MessageCircle, MessageSquarePlus, Pencil,
  Phone, Plus, Search, Send, Star, Target, Trash2, Wand2,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import QuickNoteButton from "@/components/QuickNoteButton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MasterDataSelect from "@/components/MasterDataSelect";
import { useMasterList } from "@/hooks/useMasterData";
import { formatINR, formatINRCompact, formatNumber, formatDate, formatDateTime, dueLabel } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import TonalBadge from "@/components/TonalBadge";
import ChangeHistory from "@/components/ChangeHistory";
import RecordTasksTab from "@/components/RecordTasksTab";
import { badgeStyle } from "@/lib/badgeColor";

// Editable dropdown whose trigger is tinted with the selected value's Badge Color.
function BadgeSelect({ listType, value, onChange, placeholder, testid }) {
  const { data = [] } = useMasterList(listType);
  const current = data.find((d) => d.id === value);
  const style = current?.badge_color ? badgeStyle(current.badge_color) : undefined;
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="min-w-[170px] w-auto font-semibold border" style={style} data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {data.map((d) => (
          <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function AddAssetOfferDialog({ open, onClose, leadId }) {
  const qc = useQueryClient();
  const [query, setQuery] = useState("");
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [statusId, setStatusId] = useState("");
  const [confirmStale, setConfirmStale] = useState(false);

  const { data: assets = [] } = useQuery({
    queryKey: ["asset-offer-search", query],
    queryFn: async () => (await api.get("/assets", { params: query ? { q: query } : {} })).data,
    enabled: open,
  });

  const reset = () => {
    setQuery(""); setSelectedAsset(null); setStatusId(""); setConfirmStale(false);
  };
  const handleClose = () => { reset(); onClose(); };

  const add = useMutation({
    mutationFn: async () => api.post(`/offers/by-lead/${leadId}/assets`, {
      asset_id: selectedAsset.id,
      offer_status_id: statusId || undefined,
      confirm_stale: confirmStale,
    }),
    onSuccess: (res) => {
      toast.success(res.data?.reoffered ? "Asset re-offered to lead" : "Asset offered to lead");
      qc.invalidateQueries({ queryKey: ["lead-offered-assets", leadId] });
      qc.invalidateQueries({ queryKey: ["offered", "clients", selectedAsset.id] });
      handleClose();
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail || "Failed";
      if (e?.response?.status === 409 && detail.toLowerCase().includes("confirm to offer")) {
        setConfirmStale(true);
        toast.warning(detail);
      } else {
        toast.error(detail);
      }
    },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-lg" data-testid="lead-add-asset-dialog">
        <DialogHeader><DialogTitle>Offer an Asset to this Lead</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search assets by name, code, address…"
            className="pl-8"
            data-testid="lead-add-asset-search"
          />
        </div>
        <div className="max-h-56 overflow-y-auto border border-slate-200 rounded divide-y divide-slate-100">
          {assets.length === 0 ? (
            <div className="p-4 text-sm text-slate-500">No assets found.</div>
          ) : assets.map((a) => (
            <label key={a.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer" data-testid={`lead-add-asset-option-${a.id}`}>
              <input type="radio" name="asset-offer-pick" checked={selectedAsset?.id === a.id} onChange={() => setSelectedAsset(a)} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{a.name}</div>
                <div className="text-[11px] text-slate-500 font-mono-num">{a.asset_code}</div>
              </div>
            </label>
          ))}
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Offer Status</div>
          <div className="mt-1">
            <MasterDataSelect listType="offer_status" value={statusId} onChange={setStatusId} placeholder="Offered (default)" testId="lead-add-asset-status" />
          </div>
        </div>
        {confirmStale && (
          <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="lead-add-asset-stale-warning">
            Asset is not Active — logging the offer anyway on submit.
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button onClick={() => add.mutate()} disabled={!selectedAsset || add.isPending} className="bg-slate-900 hover:bg-slate-800" data-testid="lead-add-asset-submit">
            {confirmStale ? "Offer anyway" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function useLabel(listType, id) {
  const { data = [] } = useMasterList(listType);
  return useMemo(() => data.find((d) => d.id === id)?.label || "—", [data, id]);
}

function FieldRow({ label, children }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-1 sm:gap-4 py-2.5 border-b border-slate-100 last:border-0">
      <div className="label-eyebrow !text-slate-500 pt-0.5">{label}</div>
      <div className="text-sm text-slate-900">{children}</div>
    </div>
  );
}

function ActivityLogForm({ leadId, prefillType, onLogged }) {
  const [form, setForm] = useState({ activity_type_id: "", comments: "", outcome_id: "", next_contact_date: "" });
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (prefillType) setForm((f) => ({ ...f, activity_type_id: prefillType }));
  }, [prefillType]);

  const submit = async () => {
    if (!form.activity_type_id) {
      toast.error("Activity Type is required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.next_contact_date) payload.next_contact_date = new Date(payload.next_contact_date).toISOString();
      else payload.next_contact_date = null;
      if (!payload.comments) payload.comments = null;
      if (!payload.outcome_id) payload.outcome_id = null;
      await api.post(`/leads/${leadId}/activities`, payload);
      toast.success("Activity logged");
      setForm({ activity_type_id: "", comments: "", outcome_id: "", next_contact_date: "" });
      onLogged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5">
      <div className="label-eyebrow">Log Activity</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 mb-1">Activity Type</div>
          <MasterDataSelect listType="activity_type" value={form.activity_type_id} onChange={(v) => setForm({ ...form, activity_type_id: v })} testId="log-activity-type" />
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 mb-1">Outcome</div>
          <MasterDataSelect listType="activity_outcome" value={form.outcome_id} onChange={(v) => setForm({ ...form, outcome_id: v })} testId="log-activity-outcome" />
        </div>
        <div className="md:col-span-2">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 mb-1">Comments</div>
          <Textarea rows={2} value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} placeholder="What happened…" data-testid="log-activity-comments" />
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 mb-1">Next Contact Date/Time</div>
          <Input type="datetime-local" value={form.next_contact_date} onChange={(e) => setForm({ ...form, next_contact_date: e.target.value })} className="font-mono-num" data-testid="log-activity-next-date" />
        </div>
        <div className="flex items-end">
          <Button onClick={submit} disabled={submitting} className="bg-slate-900 hover:bg-slate-800" data-testid="log-activity-submit">
            <Send className="h-3.5 w-3.5 mr-1.5" /> Log Activity
          </Button>
        </div>
      </div>
    </div>
  );
}

function MatchingAssets({ leadId }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["lead-matches", leadId],
    queryFn: async () => (await api.get(`/leads/${leadId}/matching-assets`)).data,
  });
  const matches = data?.matches || [];

  const share = useMutation({
    mutationFn: async ({ assetId, score }) => api.post("/matches", {
      asset_id: assetId, lead_id: leadId, match_score: score,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lead-matches", leadId] });
      toast.success("Shared with this lead");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="matching-assets-section">
      <div className="flex items-center gap-2">
        <Target className="h-4 w-4 text-[#B8935A]" />
        <div className="label-eyebrow">Top 5 Matching Assets</div>
      </div>
      <p className="text-xs text-slate-500 mt-1">Live score against Active assets. Strong matches (≥70%) are auto-created in the pipeline.</p>
      <div className="mt-3 space-y-2">
        {isLoading ? (
          [0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)
        ) : matches.length === 0 ? (
          <div className="border border-dashed border-slate-200 rounded p-6 text-center text-sm text-slate-500">
            No Active assets to match against yet.
          </div>
        ) : matches.map((m) => (
          <div
            key={m.id}
            className="border border-slate-200 rounded-md p-3 hover:border-slate-400 hover:bg-slate-50"
            data-testid={`match-row-${m.id}`}
          >
            <div className="flex items-center gap-3">
              <Link to={`/assets/${m.id}`} className="flex-1 min-w-0">
                <div className="font-mono-num text-[10px] text-slate-500">{m.asset_code}</div>
                <div className="text-sm font-semibold text-slate-900 truncate">{m.name}</div>
                <div className="text-xs text-slate-500 mt-0.5 font-mono-num">
                  {formatINRCompact(m.offer_price_inr)} · {formatNumber(Math.round(m.area_sqft || 0))} sqft
                </div>
              </Link>
              <div className="text-right shrink-0">
                <div className={`font-mono-num text-2xl font-bold ${m.match_score >= 75 ? "text-emerald-700" : m.match_score >= 50 ? "text-amber-700" : "text-slate-600"}`}>
                  {m.match_score}%
                </div>
                <div className="text-[10px] text-slate-500">Match</div>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-slate-100">
              {m.existing_match_id ? (
                <Link
                  to="/kanban"
                  className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1 hover:underline"
                  data-testid={`match-status-${m.id}`}
                >
                  In pipeline: <span className="text-slate-900">{m.existing_match_status_label}</span>
                </Link>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="bg-emerald-50 border-emerald-200 hover:bg-emerald-100"
                  disabled={share.isPending}
                  onClick={() => share.mutate({ assetId: m.id, score: m.match_score })}
                  data-testid={`share-asset-${m.id}`}
                >
                  Share with this lead
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = (user?.roles || [user?.role]).includes("admin");
  const [prefillActivity, setPrefillActivity] = useState("");
  const [newComment, setNewComment] = useState("");
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  const goToActivity = () => {
    setActiveTab("activity");
    setTimeout(() => document.getElementById("log-activity")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  };

  const removeOffer = useMutation({
    mutationFn: async (offer) => api.delete(`/offers/clients/${offer.offer_id}`),
    onSuccess: (_res, offer) => {
      toast.success("Removed from Assets Offered");
      qc.invalidateQueries({ queryKey: ["lead-offered-assets", id] });
      qc.invalidateQueries({ queryKey: ["offered", "clients", offer.asset_id] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to remove"),
  });

  const { data: lead, isLoading } = useQuery({
    queryKey: ["lead", id],
    queryFn: async () => (await api.get(`/leads/${id}`)).data,
  });

  const { data: activities = [], refetch: refetchActivities } = useQuery({
    queryKey: ["lead-activities", id],
    queryFn: async () => (await api.get(`/leads/${id}/activities`)).data,
    enabled: !!lead,
  });

  const { data: comments = [], refetch: refetchComments } = useQuery({
    queryKey: ["lead-comments", id],
    queryFn: async () => (await api.get(`/leads/${id}/comments`)).data,
    enabled: !!lead,
  });

  const { data: statusHistory = [] } = useQuery({
    queryKey: ["lead-status-history", id],
    queryFn: async () => (await api.get(`/leads/${id}/status-history`)).data,
    enabled: !!lead,
  });

  const { data: offeredAssets = [] } = useQuery({
    queryKey: ["lead-offered-assets", id],
    queryFn: async () => (await api.get(`/offers/by-lead/${id}`)).data,
    enabled: !!lead,
  });

  const { data: activityTypes = [] } = useMasterList("activity_type");
  const { data: outcomes = [] } = useMasterList("activity_outcome");
  const { data: tags = [] } = useMasterList("tags");
  const tagsMap = useMemo(() => Object.fromEntries(tags.map((t) => [t.id, t.label])), [tags]);

  const sourceLabel = useLabel("lead_source", lead?.lead_source_id);
  const categoryLabel = useLabel("asset_category", lead?.asset_category_id);
  const typeLabel = useLabel("asset_type", lead?.asset_type_id);
  const areaUnit = useLabel("area_unit", lead?.area_unit_id);
  const budgetUnit = useLabel("price_unit", lead?.budget_unit_id);
  const qualityLabel = useLabel("lead_quality", lead?.lead_quality_id);
  const buyerTypeLabel = useLabel("buyer_type", lead?.buyer_type_id);
  const natureLabel = useLabel("nature_of_business", lead?.nature_of_business_id);

  const fieldMut = useMutation({
    mutationFn: async ({ key, value }) => {
      // Build a full PUT-able payload from current lead state.
      const p = { ...lead, [key]: value };
      // PUT expects only LeadIn fields — strip readonly/system fields.
      ["id", "lead_code", "area_min_sqft", "area_max_sqft", "budget_min_inr", "budget_max_inr",
       "created_by", "created_at", "updated_at", "lead_owner_name",
       "last_contact_date"].forEach((k) => delete p[k]);
      return api.put(`/leads/${id}`, p);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lead", id] });
      qc.invalidateQueries({ queryKey: ["lead-status-history", id] });
      qc.invalidateQueries({ queryKey: ["leads"] });
      toast.success("Updated");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/leads/${id}`),
    onSuccess: () => { toast.success(lead?.client_type === "institutional" ? "Client deleted" : "Lead deleted"); navigate(lead?.client_type === "institutional" ? "/clients" : "/leads"); },
  });

  const reassignMut = useMutation({
    mutationFn: async (newOwnerId) => api.patch(`/leads/${id}/owner`, { lead_owner_id: newOwnerId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["lead", id] }); toast.success("Owner reassigned"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const { data: usersList = [] } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/auth/users")).data,
    enabled: isAdmin,
  });

  const addComment = async () => {
    if (!newComment.trim()) return;
    await api.post(`/leads/${id}/comments`, { comment: newComment.trim() });
    setNewComment("");
    refetchComments();
    toast.success("Comment added");
  };

  if (isLoading || !lead) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-4">
        <Skeleton className="h-10 w-72" /><Skeleton className="h-32 w-full max-w-4xl" />
      </div>
    );
  }

  const callType = activityTypes.find((t) => t.label === "Call")?.id;
  const whatsappType = activityTypes.find((t) => t.label === "WhatsApp")?.id;
  const overdueLabel = dueLabel(lead.next_contact_date);
  const isClient = lead.client_type === "institutional";
  const baseP = isClient ? "/clients" : "/leads";
  const natureDisplay = natureLabel === "Other" ? (lead.nature_of_business_other || "Other") : natureLabel;

  return (
    <div className="p-4 sm:p-6 lg:p-8 pb-20 max-w-6xl">
      <button type="button" onClick={() => navigate(baseP)} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="back-to-list">
        <ChevronLeft className="h-3 w-3" /> Back to {isClient ? "Clients" : "Leads"}
      </button>

      <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="font-mono-num text-xs text-slate-500" data-testid="lead-code">{lead.lead_code}</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mt-1" data-testid="lead-name">{lead.lead_name}</h1>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {isClient && lead.nature_of_business_id && (
              <TonalBadge listType="nature_of_business" id={lead.nature_of_business_id} data-testid="client-nature-badge" />
            )}
            {isClient && natureLabel === "Other" && lead.nature_of_business_other && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border bg-slate-100 text-slate-700 border-slate-200">{lead.nature_of_business_other}</span>
            )}
            {lead.lead_closed && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border bg-slate-100 text-slate-700 border-slate-200">Closed</span>
            )}
            <span className="text-xs text-slate-500">
              Owner: <span className="font-semibold text-slate-700">{lead.lead_owner_name}</span> · {formatDateTime(lead.created_at)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate(`${baseP}/${id}/edit`)} data-testid="edit-lead-button">
            <Pencil className="h-4 w-4 mr-1.5" /> Edit
          </Button>
          <QuickNoteButton recordType={isClient ? "Client" : "Lead"} recordId={id} recordName={lead.lead_name} size="default" />
          {isAdmin && (
            <Button variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" onClick={() => { if (window.confirm(isClient ? "Delete this client?" : "Delete this lead?")) deleteMut.mutate(); }} data-testid="delete-lead-button">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Quick-edit attributes — colored by taxonomy badge_color */}
      <div className="mt-4 flex flex-wrap items-end gap-3" data-testid="lead-quick-edit-row">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Status</div>
          <BadgeSelect listType="lead_status" value={lead.lead_status_id} placeholder="Set status" testid="lead-status-change"
            onChange={(v) => fieldMut.mutate({ key: "lead_status_id", value: v })} />
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Warmth</div>
          <BadgeSelect listType="lead_warmth" value={lead.lead_warmth_id} placeholder="Set warmth" testid="lead-warmth-change"
            onChange={(v) => fieldMut.mutate({ key: "lead_warmth_id", value: v })} />
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Quality</div>
          <BadgeSelect listType="lead_quality" value={lead.lead_quality_id} placeholder="Set quality" testid="lead-quality-change"
            onChange={(v) => fieldMut.mutate({ key: "lead_quality_id", value: v })} />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <Button asChild variant="outline" className="bg-emerald-50 border-emerald-200 hover:bg-emerald-100">
          <a href={`tel:${lead.contact_no}`} onClick={() => { setPrefillActivity(callType); goToActivity(); }} data-testid="quick-call">
            <Phone className="h-3.5 w-3.5 mr-1.5" /> Call
          </a>
        </Button>
        <Button asChild variant="outline" className="bg-green-50 border-green-200 hover:bg-green-100">
          <a href={`https://wa.me/${lead.contact_no.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" onClick={() => { setPrefillActivity(whatsappType); goToActivity(); }} data-testid="quick-whatsapp">
            <MessageCircle className="h-3.5 w-3.5 mr-1.5" /> WhatsApp
          </a>
        </Button>
        <Button variant="outline" onClick={goToActivity} data-testid="quick-log-activity">
          <Wand2 className="h-3.5 w-3.5 mr-1.5" /> Log Activity
        </Button>
        {overdueLabel && (
          <div className={`text-xs font-semibold ml-auto ${overdueLabel.tone === "critical" ? "text-red-700" : overdueLabel.tone === "warning" ? "text-amber-700" : "text-slate-600"}`} data-testid="next-contact-label">
            <Clock className="h-3 w-3 inline mr-1" /> Next contact: {formatDateTime(lead.next_contact_date)} ({overdueLabel.text})
          </div>
        )}
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-slate-100 w-full justify-start overflow-x-auto">
              <TabsTrigger value="overview" data-testid="tab-lead-overview">Overview</TabsTrigger>
              {!isClient && <TabsTrigger value="qualification" data-testid="tab-lead-qualification">Qualification</TabsTrigger>}
              <TabsTrigger value="activity" data-testid="tab-lead-activity">Activity ({activities.length})</TabsTrigger>
              <TabsTrigger value="offered" data-testid="tab-lead-offered">Assets Offered ({offeredAssets.length})</TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-lead-history">Status History</TabsTrigger>
              <TabsTrigger value="changes" data-testid="tab-lead-changes">Change History</TabsTrigger>
              <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4 space-y-5">
              {isClient && (
                <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="client-contact-persons">
                  <div className="label-eyebrow">Contact Persons</div>
                  <div className="mt-3 space-y-2">
                    {(lead.contact_persons || []).length === 0 && <div className="text-sm text-slate-400">No contact persons added.</div>}
                    {(lead.contact_persons || []).map((p, i) => {
                      const digits = (p.contact_number || "").replace(/\D/g, "");
                      return (
                        <div key={i} className="flex items-center gap-3 flex-wrap py-2 border-b border-slate-100 last:border-0" data-testid={`client-cp-${i}`}>
                          <div className="flex items-center gap-2 min-w-[180px]">
                            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">{(p.name || "?").charAt(0).toUpperCase()}</span>
                            <span className="text-sm font-semibold text-slate-900">{p.name || "—"}</span>
                          </div>
                          <span className="text-xs text-slate-500 min-w-[120px]">{p.designation || "—"}</span>
                          <span className="font-mono-num text-sm text-slate-700 flex-1">{p.contact_number || "—"}</span>
                          {p.contact_number && (
                            <div className="flex items-center gap-1.5">
                              <a href={`tel:${p.contact_number}`} className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50" data-testid={`client-cp-call-${i}`} title="Call"><Phone className="h-3.5 w-3.5" /></a>
                              <a href={`https://wa.me/${digits}`} target="_blank" rel="noreferrer" className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid={`client-cp-whatsapp-${i}`} title="WhatsApp"><MessageCircle className="h-3.5 w-3.5" /></a>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {isClient && lead.office_address && (
                <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="client-office-address">
                  <div className="flex items-center justify-between gap-2">
                    <div className="label-eyebrow">Office Address</div>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(`📍 ${lead.lead_name}\n${lead.office_address}\n\nhttps://maps.google.com/?q=${encodeURIComponent(lead.office_address)}`)}`}
                      target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 border border-emerald-200 rounded-md px-2.5 py-1.5 hover:bg-emerald-50"
                      data-testid="client-share-location"
                    >
                      <MessageCircle className="h-3.5 w-3.5" /> Share Location
                    </a>
                  </div>
                  <div className="mt-2 text-sm text-slate-700 whitespace-pre-line">{lead.office_address}</div>
                </div>
              )}

              {isClient && lead.brief_note && (
                <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="client-brief-note">
                  <div className="label-eyebrow">Brief Note</div>
                  <div className="mt-2 text-sm text-slate-700 whitespace-pre-line">{lead.brief_note}</div>
                </div>
              )}

              <div className="bg-white border border-slate-200 rounded-md p-5">
                <div className="label-eyebrow">Identification</div>
                <div className="mt-2">
                  <FieldRow label="Source">{sourceLabel}{lead.lead_source_detail ? ` · ${lead.lead_source_detail}` : ""}</FieldRow>
                  <FieldRow label={isClient ? "Main Office" : "Contact"}><span className="font-mono-num">{lead.contact_no}</span>{lead.alternate_no && <span className="text-slate-500 ml-2">· alt {lead.alternate_no}</span>}</FieldRow>
                  {lead.email && <FieldRow label="Email">{lead.email}</FieldRow>}
                  {isClient
                    ? (natureDisplay && <FieldRow label="Nature of Business">{natureDisplay}</FieldRow>)
                    : (lead.company_name && <FieldRow label="Company">{lead.company_name}</FieldRow>)}
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-md p-5">
                <div className="label-eyebrow">Requirement</div>
                <div className="mt-2">
                  <FieldRow label="Category / Type">{categoryLabel} · {typeLabel}</FieldRow>
                  <FieldRow label="Area Range">
                    <span className="font-mono-num">{lead.area_min}–{lead.area_max} {areaUnit}</span>
                    <span className="text-xs text-slate-500 ml-2">= {formatNumber(Math.round(lead.area_min_sqft))}–{formatNumber(Math.round(lead.area_max_sqft))} sqft</span>
                  </FieldRow>
                  <FieldRow label="Budget Range">
                    <span className="font-mono-num">{lead.budget_min}–{lead.budget_max} {budgetUnit}</span>
                    <span className="text-xs text-slate-500 ml-2">= {formatINR(lead.budget_min_inr)} – {formatINR(lead.budget_max_inr)}</span>
                  </FieldRow>
                  <CitiesAndVillagesRow lead={lead} />
                  {lead.short_description && <FieldRow label="Description"><div className="whitespace-pre-line">{lead.short_description}</div></FieldRow>}
                </div>
              </div>

              <CommentsCard
                leadId={id}
                comments={comments}
                newComment={newComment}
                setNewComment={setNewComment}
                onSubmit={addComment}
              />
            </TabsContent>

            {!isClient && (
            <TabsContent value="qualification" className="mt-4">
              <div className="bg-white border border-slate-200 rounded-md p-5">
                <div className="label-eyebrow">Qualification Details</div>
                <div className="mt-2">
                  {lead.buyer_type_id && <FieldRow label="Buyer Type">{buyerTypeLabel}</FieldRow>}
                  {lead.risk_appetite && <FieldRow label="Risk Appetite">{lead.risk_appetite}</FieldRow>}
                  {lead.investment_horizon && <FieldRow label="Investment Horizon">{lead.investment_horizon}</FieldRow>}
                  {lead.urgency && <FieldRow label="Urgency">{lead.urgency}</FieldRow>}
                  {lead.min_discount_required != null && <FieldRow label="Min Discount Required"><span className="font-mono-num">{lead.min_discount_required}%</span></FieldRow>}
                  {lead.brokerage_type && <FieldRow label="Brokerage">{lead.brokerage_type} · <span className="font-mono-num">{lead.brokerage_rate ?? "—"}</span> · {lead.brokerage_payable_by}</FieldRow>}
                  {lead.lead_quality_id && <FieldRow label="Lead Quality">{qualityLabel}</FieldRow>}
                  {(lead.tag_ids || []).length > 0 && (
                    <FieldRow label="Tags">
                      <div className="flex flex-wrap gap-1.5">
                        {lead.tag_ids.map((tid) => (
                          <span key={tid} className="inline-flex items-center px-2 py-0.5 text-xs bg-slate-100 border border-slate-200 rounded">
                            {tagsMap[tid] || tid}
                          </span>
                        ))}
                      </div>
                    </FieldRow>
                  )}
                </div>
              </div>
            </TabsContent>
            )}

            <TabsContent value="activity" className="mt-4 space-y-3">
              <div id="log-activity">
                <ActivityLogForm
                  leadId={id}
                  prefillType={prefillActivity}
                  onLogged={() => {
                    refetchActivities();
                    refetchComments();
                    qc.invalidateQueries({ queryKey: ["lead", id] });
                    qc.invalidateQueries({ queryKey: ["lead-comments", id] });
                    setPrefillActivity("");
                  }}
                />
              </div>
              <div className="bg-white border border-slate-200 rounded-md p-5">
                <div className="label-eyebrow">Activity History</div>
                {activities.length === 0 ? (
                  <div className="text-sm text-slate-500 py-6 text-center">No activities yet.</div>
                ) : (
                  <div className="mt-3 divide-y divide-slate-100">
                    {activities.map((a) => (
                      <ActivityRow key={a.id} a={a} activityTypes={activityTypes} outcomes={outcomes} />
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="offered" className="mt-4">
              <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="lead-offered-assets">
                <div className="flex items-center justify-between">
                  <div className="label-eyebrow">Assets Offered</div>
                  <Button size="sm" variant="outline" onClick={() => setShowAddAsset(true)} data-testid="lead-add-asset-button">
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Asset
                  </Button>
                </div>
                {offeredAssets.length === 0 ? (
                  <div className="text-sm text-slate-500 py-6 text-center">No assets offered to this lead yet.</div>
                ) : (
                  <table className="data-table w-full mt-3">
                    <thead><tr>
                      <th className="label-eyebrow !text-slate-600">Asset</th>
                      <th className="label-eyebrow !text-slate-600">Category</th>
                      <th className="label-eyebrow !text-slate-600">City</th>
                      <th className="label-eyebrow !text-slate-600">Offered</th>
                      <th className="label-eyebrow !text-slate-600">Status</th>
                      <th className="label-eyebrow !text-slate-600 text-right">Action</th>
                    </tr></thead>
                    <tbody>
                      {offeredAssets.map((o) => (
                        <tr key={o.offer_id} className="border-t border-slate-100" data-testid={`lead-offer-row-${o.offer_id}`}>
                          <td className="text-sm font-medium text-slate-900"><Link to={`/assets/${o.asset_id}`} className="hover:underline">{o.asset_name}</Link><div className="text-[10px] text-slate-400 font-mono-num">{o.asset_code}</div></td>
                          <td className="text-sm text-slate-600">{o.asset_category_label || "—"}</td>
                          <td className="text-sm text-slate-600">{o.city_label || "—"}</td>
                          <td className="text-sm text-slate-600 font-mono-num">{formatDate(o.offered_date)}</td>
                          <td><span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-slate-100 text-slate-700 border-slate-200">{o.offer_status_label}</span></td>
                          <td className="text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-[11px] text-red-700 hover:bg-red-50"
                              disabled={removeOffer.isPending}
                              onClick={() => { if (window.confirm("Remove this asset from the lead's Assets Offered? This deletes the offer record (the Asset's Offered To panel updates too).")) removeOffer.mutate(o); }}
                              data-testid={`lead-offer-remove-${o.offer_id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <AddAssetOfferDialog open={showAddAsset} onClose={() => setShowAddAsset(false)} leadId={id} />
            </TabsContent>


            <TabsContent value="history" className="mt-4">
              <div className="bg-white border border-slate-200 rounded-md p-5">
                <div className="label-eyebrow">Status History</div>
                {statusHistory.length === 0 ? (
                  <div className="text-sm text-slate-500 py-6 text-center">No history yet.</div>
                ) : (
                  <div className="mt-3 divide-y divide-slate-100">
                    {statusHistory.map((s) => (
                      <div key={s.id} className="py-2.5 flex items-center gap-3 text-sm">
                        <div className="font-mono-num text-[11px] text-slate-500 w-32">{formatDateTime(s.changed_at)}</div>
                        <div className="flex-1">
                          <span className="text-slate-500">{s.old_status_label || "—"}</span>
                          <span className="mx-1.5 text-slate-400">→</span>
                          <span className="font-semibold text-slate-900">{s.new_status_label || "—"}</span>
                        </div>
                        <div className="text-xs text-slate-500">by {s.changed_by_name || "system"}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="changes" className="mt-4">
              <ChangeHistory entityType="Lead" entityId={id} />
            </TabsContent>
            <TabsContent value="tasks" className="mt-4">
              <RecordTasksTab recordType="Lead" recordId={id} recordName={lead.lead_name} />
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-5">
          <MatchingAssets leadId={id} />
          {isAdmin && (
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <div className="label-eyebrow">Reassign Lead Owner</div>
              <Select value={lead.lead_owner_id} onValueChange={(v) => reassignMut.mutate(v)}>
                <SelectTrigger className="mt-3" data-testid="reassign-owner-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {usersList.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ActivityRow({ a, activityTypes, outcomes }) {
  const type = activityTypes.find((t) => t.id === a.activity_type_id)?.label || "—";
  const outcome = outcomes.find((o) => o.id === a.outcome_id)?.label;
  return (
    <div className="py-3 flex gap-3" data-testid={`activity-row-${a.id}`}>
      <div className="font-mono-num text-[11px] text-slate-500 w-32 shrink-0 pt-0.5">{formatDateTime(a.logged_at)}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm">
          <span className="font-semibold text-slate-900">{type}</span>
          {outcome && <span className="ml-2 text-slate-500">· {outcome}</span>}
          {a.next_contact_date && <span className="ml-2 text-xs text-slate-500">Next: {formatDateTime(a.next_contact_date)}</span>}
        </div>
        {a.comments && <div className="text-sm text-slate-700 mt-1 whitespace-pre-line">{a.comments}</div>}
        <div className="text-[10px] text-slate-400 mt-1">by {a.logged_by_name || "system"}</div>
      </div>
    </div>
  );
}

function CitiesAndVillagesRow({ lead }) {
  const { data: cities = [] } = useMasterList("city");
  // Villages — fetch all (any cities) — using include_inactive false ok
  const cityNames = (lead.preferred_city_ids || [])
    .map((cid) => cities.find((c) => c.id === cid)?.label)
    .filter(Boolean);
  return (
    <FieldRow label="Preferred Locations">
      <div className="text-sm">
        <div>{cityNames.join(", ") || "—"}</div>
        <VillagesList lead={lead} />
      </div>
    </FieldRow>
  );
}

function VillagesList({ lead }) {
  const { data: villages = [] } = useMasterList("village_town");
  const labels = (lead.preferred_village_ids || [])
    .map((vid) => villages.find((v) => v.id === vid)?.label)
    .filter(Boolean);
  if (labels.length === 0) return null;
  return <div className="text-xs text-slate-500 mt-0.5">Villages: {labels.join(", ")}</div>;
}

function CommentsCard({ leadId, comments, newComment, setNewComment, onSubmit }) {
  const [expanded, setExpanded] = useState(false);
  const showing = expanded ? comments : comments.slice(0, 1);
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5">
      <div className="label-eyebrow">Comments</div>
      <div className="mt-3 flex gap-2">
        <Input value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Add a comment…" data-testid="add-comment-input" />
        <Button onClick={onSubmit} disabled={!newComment.trim()} className="bg-slate-900 hover:bg-slate-800" data-testid="add-comment-button">
          <MessageSquarePlus className="h-3.5 w-3.5 mr-1.5" /> Add
        </Button>
      </div>
      {comments.length === 0 ? (
        <div className="text-xs text-slate-500 mt-4">No comments yet.</div>
      ) : (
        <div className="mt-4 space-y-2">
          {showing.map((c) => (
            <div key={c.id} className="border border-slate-100 rounded p-3" data-testid={`comment-${c.id}`}>
              <div className="text-sm text-slate-900 whitespace-pre-line">{c.comment}</div>
              <div className="text-[10px] text-slate-500 mt-1">{c.logged_by_name || "user"} · {formatDateTime(c.logged_at)}</div>
            </div>
          ))}
          {comments.length > 1 && (
            <button type="button" onClick={() => setExpanded((e) => !e)} className="text-xs font-semibold text-slate-700 hover:underline" data-testid="comments-toggle">
              {expanded ? "Show only latest" : `View all ${comments.length} comments`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
